// City-service landing pages — keyword-rich URLs targeting local intent
// (e.g. "cataract surgery in varanasi", "pediatric ophthalmologist varanasi").
// Each page is a distinct entity from the /expertise/<slug> content: it stitches
// together the clinical service + local intent (cost, insurance, appointment
// pattern in Varanasi) and cross-links back to the canonical expertise page.
//
// SEO strategy:
//  - Unique H1 with city + service keyword (verbatim search query)
//  - Unique meta title/description (< 60 / < 155 chars)
//  - MedicalProcedure + Physician JSON-LD linked via @id to the site knowledge graph
//  - Breadcrumb: Home > Services in Varanasi > <service>
//  - Cross-link to the canonical /expertise/<slug> page as `mainEntityOfPage`

export const LANDING_PAGES = [
  {
    slug: "premium-cataract-surgery-in-varanasi",
    h1: "Premium Cataract Surgery in Varanasi",
    metaTitle: "Premium Cataract Surgery in Varanasi — Dr. Shantanu Gupta",
    metaDescription:
      "Micro-Incision Cataract Surgery (MICS) in Varanasi by Dr. Shantanu Kumar Gupta (MAMC, New Delhi trained). 10,000+ surgeries. Toric, EDOF & Trifocal IOL options. Cashless & Ayushman Bharat accepted.",
    tagline: "Bladeless, stitchless cataract surgery — MAMC-trained precision, delivered in Varanasi.",
    keyword: "cataract surgery Varanasi",
    procedureName: "Micro-Incision Cataract Surgery",
    procedureCategory: "SurgicalProcedure",
    canonicalExpertise: "premium-cataract-surgery",
    consultantSlug: "shantanu",
    heroBullets: [
      "MICS 2.2 mm phacoemulsification · same-day discharge",
      "Monofocal / Toric / EDOF / Trifocal IOL options — brand-agnostic counselling",
      "Cashless approvals with major TPAs · Ayushman Bharat (PM-JAY) accepted",
      "10,000+ cataract surgeries · 12+ years of clinical experience",
    ],
    intro:
      "Cataract remains the leading cause of reversible blindness across eastern Uttar Pradesh. At Shantanu Netralaya Eye Hospital in Durgakund, Varanasi, we perform Micro-Incision Cataract Surgery (MICS) — a 2.2 mm bladeless, stitchless technique that lets most patients read comfortably within 24 hours of surgery. Every case is planned personally by Dr. Shantanu Kumar Gupta (MBBS, MS Ophthalmology — Maulana Azad Medical College & Guru Nanak Eye Centre, New Delhi; DNB; FICO Merit).",
    sections: [
      {
        heading: "What makes our cataract surgery different",
        body:
          "We do not run a conveyor belt. Every patient gets a full IOL counselling session with the surgeon (not a coordinator) covering the trade-offs between monofocal, toric, extended-depth-of-focus (EDOF) and trifocal implants — and their honest impact on your night driving, screen reading and spectacle dependence. Biometry is performed with the latest optical technology and cross-checked before your surgery date. The clinic accepts most major TPAs on a cashless basis and is empanelled for Ayushman Bharat (PM-JAY).",
      },
      {
        heading: "Cataract surgery cost in Varanasi — what to expect",
        body:
          "Total out-of-pocket depends on your IOL choice. Ayushman Bharat / PM-JAY covers only an IOL implant worth approximately ₹3,000 for eligible beneficiaries — all other scheme details are clarified at the hospital. Premium IOL options (toric, EDOF, trifocal) are priced transparently and discussed openly before you commit. You receive a printed estimate before surgery is scheduled. We do not charge for the initial IOL counselling consultation.",
      },
    ],
    faqs: [
      {
        q: "How soon after cataract surgery can I read or return to work?",
        a: "Most patients read newspaper-size text comfortably within 24 hours. Desk / laptop work resumes within 2–3 days; heavy lifting and dust-heavy environments should wait 2 weeks.",
      },
      {
        q: "Do you offer laser (femto) cataract surgery in Varanasi?",
        a: "We perform gold-standard manual MICS phacoemulsification with sub-millimeter precision — the technique recommended by most academic centres worldwide. Where a specific clinical indication for femto assistance exists, we discuss options openly. Modern MICS phaco outcomes match femto in the vast majority of cases.",
      },
      {
        q: "Is my cataract surgery covered by insurance / Ayushman Bharat?",
        a: "Yes. Shantanu Netralaya is empanelled for Ayushman Bharat (PM-JAY) and offers cashless approvals with most major TPAs. Bring your policy card + photo ID to the consultation — the desk handles paperwork on the same day.",
      },
    ],
    ctaMessage:
      "Hello Dr. Shantanu, I would like to book a cataract surgery consultation in Varanasi.",
  },
  {
    slug: "medical-retina-specialist-in-varanasi",
    h1: "Medical Retina & Retina Injections in Varanasi",
    metaTitle: "Retina Specialist in Varanasi — Dr. Shantanu Kumar Gupta",
    metaDescription:
      "Medical retina care in Varanasi — OCT-guided evaluation, diabetic retinopathy, AMD, retinal vein occlusion and intravitreal anti-VEGF injections. Dr. Shantanu Kumar Gupta, MAMC New Delhi trained.",
    tagline: "OCT-guided diagnosis. Evidence-based treatment. Same-day injections when needed.",
    keyword: "retina specialist Varanasi",
    procedureName: "Intravitreal Anti-VEGF Injection",
    procedureCategory: "TherapeuticProcedure",
    canonicalExpertise: "medical-retina",
    consultantSlug: "shantanu",
    heroBullets: [
      "OCT / OCT-A imaging on the same visit as your consult",
      "Diabetic retinopathy screening & laser + injection protocols",
      "Wet AMD, retinal vein occlusion (RVO), central serous retinopathy",
      "Anti-VEGF injections given in a sterile in-office setting",
    ],
    intro:
      "The retina is the most fragile — and most under-diagnosed — part of the eye. In eastern Uttar Pradesh, undetected diabetic retinopathy remains a leading cause of preventable adult blindness. At Shantanu Netralaya, every retina consult begins with high-resolution OCT (Optical Coherence Tomography) so that we see, and show you, the exact layer of retina involved — before any treatment plan is proposed.",
    sections: [
      {
        heading: "Conditions we manage",
        body:
          "Diabetic retinopathy (all stages, including macular edema) · Age-related macular degeneration (dry and wet, with anti-VEGF therapy) · Retinal vein occlusion (central and branch) · Central serous retinopathy · Vitreo-retinal follow-up after surgery elsewhere · Second-opinion review of retina reports from other centres.",
      },
      {
        heading: "Retina injections — what to expect",
        body:
          "Anti-VEGF intravitreal injections are performed in a sterile procedure room using topical anaesthesia. The whole visit — check-in, dilation, OCT, counselling, injection, post-injection review — typically takes 90 minutes. You resume most normal activity the same evening. Follow-up is scheduled at 4 weeks by default and adjusted per response.",
      },
    ],
    faqs: [
      {
        q: "Do I need a referral for a retina consult in Varanasi?",
        a: "No referral is needed. You can walk in with any recent reports (OCT, blood-sugar / HbA1c, prior prescriptions) — we will review them and, if needed, repeat any imaging on the same visit.",
      },
      {
        q: "How many retina injections will I need?",
        a: "Wet AMD, diabetic macular edema and RVO typically respond over a series of 3 monthly loading doses followed by an as-needed maintenance schedule. You get a written plan after your first OCT — no open-ended commitments.",
      },
    ],
    ctaMessage:
      "Hello Dr. Shantanu, I would like a retina consultation in Varanasi. I would like to bring my OCT / diabetic reports.",
  },
  {
    slug: "squint-surgery-in-varanasi",
    h1: "Squint (Strabismus) Surgery in Varanasi",
    metaTitle: "Squint Surgery in Varanasi — Dr. Manisha Mishra",
    metaDescription:
      "Squint / strabismus correction in Varanasi for children and adults. Consultant Dr. Manisha Mishra — pediatric ophthalmology & strabismus specialist. Cashless & Ayushman Bharat accepted.",
    tagline: "Cosmetic and functional squint correction — for children, teens and adults.",
    keyword: "squint surgery Varanasi",
    procedureName: "Strabismus Surgery",
    procedureCategory: "SurgicalProcedure",
    canonicalExpertise: null,
    consultantSlug: "manisha",
    heroBullets: [
      "Squint evaluation for children (from age 2) and adults",
      "Non-surgical management first — patching, glasses, prisms where appropriate",
      "Muscle-recession / resection surgery under monitored anaesthesia",
      "Post-operative binocular vision training to lock in the alignment",
    ],
    intro:
      "Squint (also called strabismus) is more than a cosmetic concern — in children, an uncorrected squint can lead to amblyopia (a permanently weaker eye), and in adults it commonly causes double vision, tired eyes and social self-consciousness. Squint services at Shantanu Netralaya are led by Consultant Dr. Manisha Mishra, an experienced pediatric ophthalmologist and squint surgeon.",
    sections: [
      {
        heading: "When to bring a child for a squint check-up",
        body:
          "Any persistent eye deviation beyond 4 months of age, a child who tilts the head to see, watery eyes, closing one eye in bright sun, or difficulty catching a ball — all warrant a squint evaluation. Early treatment (before age 8) gives the best chance of restoring full binocular vision.",
      },
      {
        heading: "Adult squint — cosmetic and functional",
        body:
          "Adults with long-standing squint often assume nothing can be done. That is a myth. Modern strabismus surgery in adults offers excellent cosmetic alignment, and in many cases restores binocular fusion and eliminates double vision. Cases are planned individually after a full orthoptic assessment.",
      },
    ],
    faqs: [
      {
        q: "Is squint surgery painful for a child?",
        a: "Squint surgery is performed under a short general anaesthetic for children. There is mild redness and a scratchy sensation for 2–3 days afterwards, managed with eye drops. Most children return to school in a week.",
      },
      {
        q: "Will the squint come back after surgery?",
        a: "The alignment is engineered to hold long-term, but a small residual drift can occur in about 10% of cases. This is why we follow up carefully in the first year and, in select cases, use orthoptic exercises or a small touch-up.",
      },
    ],
    ctaMessage:
      "Hello, I would like to book a squint (strabismus) consultation with Dr. Manisha Mishra at Shantanu Netralaya, Varanasi.",
  },
  {
    slug: "pediatric-ophthalmologist-in-varanasi",
    h1: "Pediatric Ophthalmologist in Varanasi",
    metaTitle: "Pediatric Eye Specialist in Varanasi — Dr. Manisha Mishra",
    metaDescription:
      "Complete pediatric eye care in Varanasi — refractive check-up, amblyopia (lazy eye), squint, blocked tear ducts and childhood cataract. Consultant Dr. Manisha Mishra.",
    tagline: "Gentle, child-friendly eye care — from infants to teenagers.",
    keyword: "pediatric ophthalmologist Varanasi",
    procedureName: "Pediatric Ophthalmology Consultation",
    procedureCategory: "MedicalProcedure",
    canonicalExpertise: null,
    consultantSlug: "manisha",
    heroBullets: [
      "First eye check for infants, toddlers, school-age and teens",
      "Amblyopia (lazy eye) diagnosis and patching / atropine therapy",
      "Refractive errors, glasses adaptation and myopia control",
      "Blocked tear duct probing and childhood cataract referral pathway",
    ],
    intro:
      "Children are not small adults — their eyes develop rapidly through age 8, and problems missed in this window can leave a lifetime of reduced vision. Pediatric ophthalmology services at Shantanu Netralaya are led by Consultant Dr. Manisha Mishra, whose practice is dedicated to child-friendly eye examinations and squint / amblyopia care.",
    sections: [
      {
        heading: "First eye check for your child — what to expect",
        body:
          "A pediatric eye examination is nothing like an adult's. We use age-appropriate charts (pictures for pre-readers, tumbling-E for early school), a cycloplegic refraction (drops that relax the focusing muscles so glasses power is measured accurately), and a dilated retina exam. Every visit ends with a printed summary you can share with your paediatrician.",
      },
      {
        heading: "Why school eye check-ups miss so much",
        body:
          "Camp screenings usually check only distance vision. A child with one weak eye (amblyopia) can score 6/6 in the good eye and still be missed. A pediatric ophthalmology visit checks each eye separately, screens for squint, checks depth perception and dilates the eye — the only reliable way to detect amblyopia early.",
      },
    ],
    faqs: [
      {
        q: "At what age should my child's first eye check happen?",
        a: "The first pediatric eye check is recommended around age 3, or earlier if you notice head tilt, one eye wandering, watery eyes, or a family history of squint / childhood glasses. Any pediatrician-flagged concern can be assessed at any age from birth onwards.",
      },
      {
        q: "Are eye drops (cycloplegic) safe for my child?",
        a: "Yes. The dilating drops we use are age-appropriate and wear off within a few hours. Your child's near vision will be blurry and pupils larger than usual for the afternoon — plan a quiet activity at home after the appointment.",
      },
    ],
    ctaMessage:
      "Hello, I would like to book a pediatric eye check-up for my child with Dr. Manisha Mishra at Shantanu Netralaya, Varanasi.",
  },
  {
    slug: "rop-screening-in-varanasi",
    h1: "ROP Screening (Retinopathy of Prematurity) in Varanasi",
    metaTitle: "ROP Screening in Varanasi — Preterm Baby Eye Check",
    metaDescription:
      "Retinopathy of Prematurity (ROP) screening for preterm babies in Varanasi with Dr. Shantanu Kumar Gupta. Bedside NICU-friendly examination + laser referral when indicated.",
    tagline: "Every preterm baby deserves a timely ROP eye check — before the window for intervention closes.",
    keyword: "ROP screening Varanasi",
    procedureName: "Retinopathy of Prematurity Screening",
    procedureCategory: "DiagnosticProcedure",
    canonicalExpertise: null,
    consultantSlug: "shantanu",
    heroBullets: [
      "Bedside / NICU-friendly ROP examination",
      "Recommended for babies born < 34 weeks or < 2000 g",
      "First screen by 4 weeks of age (or 30 weeks post-menstrual)",
      "Same-day treatment pathway coordination when disease is detected",
    ],
    intro:
      "Retinopathy of Prematurity (ROP) is a fast-progressing retinal disease in preterm babies. Without a timely eye check, it is the single leading cause of preventable childhood blindness. Every baby born before 34 weeks, or weighing under 2000 g, needs a formal ROP screening — usually starting by 4 weeks of age. Shantanu Netralaya's ROP service is led by Dr. Shantanu Kumar Gupta, whose training in medical retina and imaging directly supports early detection and safe management.",
    sections: [
      {
        heading: "When should the first ROP screening happen?",
        body:
          "The first screening is typically at 4 weeks of chronological age (or 30–31 weeks post-menstrual age, whichever is later). Repeat visits are scheduled every 1–2 weeks depending on the initial findings — until either the retinal blood vessels have matured, or definitive treatment (laser or anti-VEGF) has been completed.",
      },
      {
        heading: "How the examination is done",
        body:
          "The baby is examined with a special lid speculum after topical anaesthetic drops. The exam takes about 5 minutes per eye. It is safe, bedside-compatible with NICU incubators, and the parent is welcome to be present. A printed report is issued the same visit.",
      },
    ],
    faqs: [
      {
        q: "Is the ROP examination safe for a very preterm baby?",
        a: "Yes — it is a well-established, minimally-invasive examination performed by specialist ophthalmologists worldwide. The alternative (missing ROP) is far more dangerous.",
      },
      {
        q: "What if my baby needs ROP treatment?",
        a: "If threshold ROP is diagnosed, treatment (laser photocoagulation or an intravitreal anti-VEGF injection) is arranged within 48–72 hours. We coordinate closely with the neonatal team throughout.",
      },
    ],
    ctaMessage:
      "Hello Dr. Shantanu, I would like to schedule an ROP screening for my preterm baby at Shantanu Netralaya, Varanasi.",
  },
  {
    slug: "comprehensive-eye-examination-in-varanasi",
    h1: "Comprehensive Eye Examination in Varanasi",
    metaTitle: "Comprehensive Eye Check-up in Varanasi — Shantanu Netralaya",
    metaDescription:
      "Full eye examination in Varanasi — refraction, cornea, lens, retina, glaucoma screening and pressure check. Same-day OCT if needed. Book with Dr. Shantanu Kumar Gupta.",
    tagline: "One thorough visit. Every layer of the eye examined. Honest advice — no unnecessary tests.",
    keyword: "eye check up Varanasi",
    procedureName: "Comprehensive Eye Examination",
    procedureCategory: "DiagnosticProcedure",
    canonicalExpertise: "general-ophthalmology",
    consultantSlug: "shantanu",
    heroBullets: [
      "Refraction & up-to-date glasses / contact lens power",
      "Slit-lamp exam of cornea, lens and anterior segment",
      "Intra-ocular pressure check (glaucoma screening)",
      "Dilated retina examination — same-day OCT if indicated",
    ],
    intro:
      "A routine eye examination is the easiest way to protect vision and catch problems early. At Shantanu Netralaya, every full check-up covers the whole eye — front to back — and ends with a plain-language explanation of what we found and what (if anything) needs follow-up. If you have been searching for an ‘eye check up near me’ or ‘eye specialist in Varanasi’, this page is for you.",
    sections: [
      {
        heading: "What is included in a comprehensive eye exam",
        body:
          "Vision testing for distance and near · glasses / contact lens power update · slit-lamp examination of cornea, iris and lens · intra-ocular pressure measurement (glaucoma screen) · dilated retina examination · advice on lifestyle, screen habits and diet. We add OCT, corneal topography or visual fields only when a finding on the basic exam warrants it — never as a routine upsell.",
      },
      {
        heading: "When to come in",
        body:
          "Adults: every 2 years if 20–40, every year after 40. Diabetics: every year regardless of age. After any sudden vision change, floaters, flashing lights or eye pain: same week. Children: age 3, again at 6, and every 2 years through school. Screen-heavy jobs: annually.",
      },
    ],
    faqs: [
      {
        q: "How long does a comprehensive eye exam take?",
        a: "About 45–60 minutes, including dilation. Please plan not to drive yourself home afterwards — dilation blurs near vision and increases light sensitivity for 3–4 hours.",
      },
      {
        q: "Do I need to bring old prescriptions or reports?",
        a: "Yes — any old prescriptions, previous fundus photos / OCT reports, or diabetes reports (HbA1c) help us make faster, better decisions. Even a photo of the old spectacle case with the power inside helps.",
      },
    ],
    ctaMessage:
      "Hello Dr. Shantanu, I would like to book a comprehensive eye examination in Varanasi.",
  },
  {
    slug: "diabetic-retinopathy-treatment-in-varanasi",
    h1: "Diabetic Retinopathy Treatment in Varanasi",
    metaTitle: "Diabetic Retinopathy Specialist in Varanasi — Dr. Shantanu",
    metaDescription:
      "Diabetic eye disease screening, laser and injection treatment in Varanasi. Every diabetic patient deserves a yearly retina check — Dr. Shantanu Kumar Gupta, MAMC New Delhi.",
    tagline: "Diabetes-related vision loss is preventable — if caught early.",
    keyword: "diabetic retinopathy Varanasi",
    procedureName: "Diabetic Retinopathy Treatment",
    procedureCategory: "MedicalProcedure",
    canonicalExpertise: "diabetic-retinopathy",
    consultantSlug: "shantanu",
    heroBullets: [
      "Same-visit dilated retina examination + OCT / OCT-A",
      "Pan-retinal photocoagulation (PRP) laser for advanced disease",
      "Intravitreal anti-VEGF injections for diabetic macular edema",
      "Coordinated shared-care notes for your diabetologist / GP",
    ],
    intro:
      "In eastern Uttar Pradesh, over 1 in 4 adults with diabetes is estimated to have some degree of diabetic retinopathy — most without knowing it, because early disease has no symptoms. A dilated retina check once every 12 months is the single most cost-effective way to protect a diabetic patient's vision.",
    sections: [
      {
        heading: "When to be screened",
        body:
          "Type 2 diabetes: from the day of diagnosis. Type 1 diabetes: within 5 years of diagnosis and yearly thereafter. Pregnancy with pre-existing diabetes: at booking and again every trimester. Any new floaters, cobwebs or sudden vision change: within 48 hours.",
      },
      {
        heading: "Treatment options",
        body:
          "Non-proliferative disease: lifestyle + tight glycemic control + follow-up. Diabetic macular edema: intravitreal anti-VEGF injections (loading dose × 3, then as-needed). Proliferative disease: pan-retinal photocoagulation laser ± anti-VEGF. Vitreous haemorrhage or tractional detachment: vitreo-retinal surgical referral.",
      },
    ],
    faqs: [
      {
        q: "I have no vision problems but I am diabetic — do I really need a retina check?",
        a: "Yes. Diabetic retinopathy is silent in its early stages. By the time vision drops, significant damage may already have occurred. A yearly dilated retina exam is the single best protection.",
      },
      {
        q: "Is laser treatment for diabetic retinopathy painful?",
        a: "Modern pan-retinal photocoagulation is performed with topical drops and mild sedation if needed. Some patients feel a snapping sensation but pain is minimal. Two sessions of 30–40 minutes are typical.",
      },
    ],
    ctaMessage:
      "Hello Dr. Shantanu, I am diabetic and would like to book a retina screening at Shantanu Netralaya, Varanasi.",
  },
  {
    slug: "complex-cataract-surgery-in-varanasi",
    h1: "Complex & Complicated Cataract Surgery in Varanasi",
    metaTitle: "Complex Cataract Surgery in Varanasi — Dr. Manisha Mishra",
    metaDescription:
      "Complex and complicated cataract surgery in Varanasi — subluxated / dislocated lenses, small-pupil cases, pediatric cataract, high myopia and post-uveitic cataracts. Consultant Dr. Manisha Mishra.",
    tagline: "When routine cataract surgery isn't enough — MAMC-trained expertise for the difficult case.",
    keyword: "complex cataract surgery Varanasi",
    procedureName: "Complex Cataract Surgery",
    procedureCategory: "SurgicalProcedure",
    canonicalExpertise: "premium-cataract-surgery",
    consultantSlug: "manisha",
    heroBullets: [
      "Subluxated / dislocated lens with capsular tension ring (CTR) or SFIOL",
      "Small-pupil cataract · pseudoexfoliation syndrome",
      "Pediatric cataract with primary IOL implantation",
      "Post-uveitic, mature white, brunescent and hyper-mature cataracts",
    ],
    intro:
      "Not every cataract is straightforward. Weak zonules, previous eye surgery, uveitis, high myopia, trauma or a child's small eye each demand surgical judgement that goes far beyond a routine phacoemulsification. At Shantanu Netralaya, complex cataract cases are personally handled by Consultant Dr. Manisha Mishra — MAMC-trained, with senior residency exposure at Guru Nanak Eye Centre to some of the most challenging anterior-segment cases in the country.",
    sections: [
      {
        heading: "Which cataracts count as 'complex'?",
        body:
          "Subluxated or dislocated crystalline lenses (Marfan's, homocystinuria, blunt trauma); pseudoexfoliation syndrome; small non-dilating pupils; previous vitreo-retinal surgery; post-uveitic cataracts; mature white or brunescent (rock-hard) lenses; pediatric cataract; extreme myopia (axial length > 26 mm) or extreme hyperopia (< 22 mm); and any second-eye cataract when the first eye had a complication elsewhere — all fall under this umbrella.",
      },
      {
        heading: "Why the choice of surgeon matters more here",
        body:
          "In a complex cataract case the difference between a routine outcome and a lifelong complication is measured in minutes of intra-operative decision-making. Dr. Manisha's training under strabismus and anterior-segment pioneers at Guru Nanak Eye Centre, combined with hands-on experience in SFIOL, capsular tension rings, iris hooks, femto assistance where indicated, and pediatric cataract implantation — gives complex cases the surgical vocabulary they deserve.",
      },
    ],
    faqs: [
      {
        q: "My last cataract surgery had a complication. Can it still be fixed?",
        a: "Very often, yes. Options include IOL exchange, scleral fixation of an IOL (SFIOL), pars-plana clean-up of vitreous, and iris repair. The first step is a detailed evaluation with imaging — no plan is proposed without it.",
      },
      {
        q: "Can this be done under Ayushman Bharat / cashless insurance?",
        a: "Complex cataract surgery is covered under Ayushman Bharat and most major TPA cashless policies. Additional supplies (capsular tension ring, iris hooks, specialty IOL) are discussed in a printed pre-op estimate.",
      },
      {
        q: "My child has a cataract — is this the right page?",
        a: "Yes. Pediatric cataract falls under complex cataract surgery. It requires a general anaesthetic and a specific pediatric IOL choice; we plan every case individually with the child's pediatrician.",
      },
    ],
    ctaMessage:
      "Hello, I would like to book a complex cataract consultation with Dr. Manisha Mishra at Shantanu Netralaya, Varanasi.",
  },
  {
    slug: "glaucoma-treatment-in-varanasi",
    h1: "Glaucoma Screening & Treatment in Varanasi",
    metaTitle: "Glaucoma Specialist in Varanasi — Shantanu Netralaya",
    metaDescription:
      "Glaucoma screening, IOP monitoring, visual-field testing and medical management in Varanasi. Painless, silent vision loss is preventable — book a screening today.",
    tagline: "The silent thief of sight. Painless. Preventable. If caught early.",
    keyword: "glaucoma treatment Varanasi",
    procedureName: "Glaucoma Screening and Management",
    procedureCategory: "MedicalProcedure",
    canonicalExpertise: "general-ophthalmology",
    consultantSlug: "shantanu",
    heroBullets: [
      "Non-contact tonometry — no drops, no puff of air discomfort",
      "Visual-field (perimetry) testing when indicated",
      "OCT of the optic nerve for early structural detection",
      "Medical management with prostaglandin analogues + follow-up plan",
    ],
    intro:
      "Glaucoma quietly narrows the visual field from the periphery inwards — often for years before the patient notices. In India, an estimated 12 million people are affected, and glaucoma is the second-leading cause of irreversible blindness. Yearly IOP + optic-nerve screening after age 40 (earlier with family history) is a small, cheap investment for a lifetime of sight.",
    sections: [
      {
        heading: "Who should be screened",
        body:
          "Everyone over 40 (yearly) · anyone with a first-degree relative with glaucoma (yearly, starting age 30) · diabetics, high myopes and steroid users (individualised) · anyone with unexplained headaches or eye pain (immediately).",
      },
      {
        heading: "How we manage confirmed glaucoma",
        body:
          "First-line therapy is usually a once-a-day prostaglandin analogue eye drop, targeting a 25–30% reduction in intra-ocular pressure. Follow-up IOP + visual-field / OCT checks are scheduled 3-monthly at first. Laser and surgical options are considered only when drops fail or are not tolerated.",
      },
    ],
    faqs: [
      {
        q: "Does glaucoma have any symptoms in the early stages?",
        a: "Rarely. That is exactly why screening after 40 matters — by the time peripheral vision loss is noticeable, significant optic-nerve damage has already occurred.",
      },
      {
        q: "Can glaucoma damage be reversed?",
        a: "Damage already done to the optic nerve is permanent, but treatment can stop further loss. This is why an early diagnosis makes such a difference.",
      },
    ],
    ctaMessage:
      "Hello Dr. Shantanu, I would like to book a glaucoma screening at Shantanu Netralaya, Varanasi.",
  },
];

// Consultants used across landing pages. Dr. Manisha Mishra is featured on
// pediatric / squint / ROP pages until a full profile page is built.
export const CONSULTANTS = {
  shantanu: {
    name: "Dr. Shantanu Kumar Gupta",
    role: "Cataract, Cornea & Medical Retina Consultant",
    credentials: "MBBS · MS Ophthalmology (MAMC, New Delhi) · DNB · FICO (Merit, ICO)",
    schemaId: "https://drshantanugupta.com/#physician",
    profileUrl: "/about",
    portrait:
      "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/cyab0eqg_6BF292C3-6EEB-49C8-BD2E-1008EDDDC0BD%202.PNG",
  },
  manisha: {
    name: "Dr. Manisha Mishra",
    role: "Consultant — Cataract, Squint & Pediatric Ophthalmology",
    credentials: "MBBS (Gold Medalist, 4 distinctions) · MS Ophthalmology (MAMC, New Delhi) · DNB · FICO (Merit)",
    schemaId: "https://drshantanugupta.com/#physician-manisha",
    profileUrl: "/team/dr-manisha-mishra",
    portrait: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/4ykhz87v_F43F41D7-9C9C-464E-90EE-6356CEF829FD.png",
    bioShort:
      "MAMC-trained ophthalmologist specialising in cataract, squint (strabismus) and pediatric ophthalmology. Senior residency at Guru Nanak Eye Centre, MAMC, under one of India's pioneers in strabismus.",
  },
};
